/**
 * v-focus-trap="visible": makes a modal (el-dialog, el-drawer) usable without a mouse.
 *
 * Element UI v2 neither moves the focus into a dialog nor keeps it there: a dialog opened
 * from a keyboard-driven list left the focus on the row behind it, Tab walked the page
 * under the mask, and Enter reached the list instead of the dialog. With the directive:
 *
 *   - opening moves the focus into the modal: to the element marked `data-autofocus`
 *     (the action Enter should trigger), otherwise onto the modal itself, so the next
 *     Tab lands on its first control;
 *   - Tab and Shift+Tab cycle through the modal's controls and never leave it;
 *   - closing hands the focus back to where it was when the modal opened.
 *
 * Bind it to the component's visibility: `el-dialog(v-focus-trap="visible" ...)`.
 * A component that focuses a field itself on open (`@opened`) keeps doing so: the
 * directive only moves the focus while it is still outside the modal.
 */

const TABBABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]',
].join(',');

const STATE = '__focusTrap';

const isVisible = (element) => !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);

const tabbables = (container) =>
    [...container.querySelectorAll(TABBABLE)].filter((element) => element.tabIndex >= 0 && isVisible(element));

// The element that carries the modal's role: the one to focus when nothing is marked.
const modalOf = (container) => container.querySelector('[role="dialog"], .el-dialog') || container;

const focusInto = (container) => {
    if (container.contains(document.activeElement)) return;
    const marked = [...container.querySelectorAll('[data-autofocus]')].find(isVisible);
    // A marked wrapper (el-upload, el-input) hands the focus to its first control.
    const control = marked && (marked.tabIndex >= 0 ? marked : tabbables(marked)[0]);
    const target = control || modalOf(container);
    if (!control && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
};

const activate = (container) => {
    const state = container[STATE];
    state.returnTo = document.activeElement;
    // After the render that shows the modal (the directive runs on the host's
    // patch, the dialog's own v-show follows in the same flush). Not
    // requestAnimationFrame: a tab that is not painted never runs it.
    setTimeout(() => focusInto(container));
};

const deactivate = (container) => {
    const { returnTo } = container[STATE];
    container[STATE].returnTo = null;
    const focusLeftBehind = container.contains(document.activeElement) || document.activeElement === document.body;
    if (returnTo && returnTo.isConnected && focusLeftBehind) {
        setTimeout(() => returnTo.focus({ preventScroll: true }));
    }
};

const onKeydown = (event) => {
    if (event.key !== 'Tab') return;
    const container = event.currentTarget;
    if (!container[STATE].active) return;
    const controls = tabbables(container);
    if (!controls.length) {
        event.preventDefault();
        return;
    }
    const index = controls.indexOf(document.activeElement);
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (index === -1) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
    }
};

export default {
    bind(element, binding) {
        element[STATE] = { active: !!binding.value, returnTo: null };
        element.addEventListener('keydown', onKeydown);
    },
    inserted(element) {
        if (element[STATE].active) activate(element);
    },
    componentUpdated(element, binding) {
        const active = !!binding.value;
        if (active === element[STATE].active) return;
        element[STATE].active = active;
        if (active) activate(element);
        else deactivate(element);
    },
    unbind(element) {
        element.removeEventListener('keydown', onKeydown);
        delete element[STATE];
    },
};
